import React, { useEffect, useState, useCallback, useMemo } from "react";
import MUIDataTable from "mui-datatables";
import {
  Button,
  IconButton,
  DialogContent,
  Dialog,
  DialogActions,
  Box,
  Chip,
  Switch,
} from "@mui/material";
import { toast } from "react-toastify";
import { Close, DeleteOutline, Edit } from "@mui/icons-material";
import Axios from "../axios";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useAuth } from "../auth/authContext";

const getExpiryStatus = (diffDays) => {
  if (diffDays === null || diffDays === undefined) return { label: "No Date", color: "default" };
  if (diffDays < 0) return { label: "Expired", color: "error" };
  if (diffDays === 0) return { label: "Expires Today", color: "error" };
  if (diffDays <= 7) return { label: `${diffDays} day${diffDays > 1 ? "s" : ""} left`, color: "warning" };
  return { label: `${diffDays} days left`, color: "success" };
};

const FeaturedHotels = () => {
  const { navData } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [opendel, setOpendel] = useState({ status: false, id: null });
  const [allHotels, setAllHotels] = useState([]);
  const [count, setCount] = useState(0);
  const [write, setWrite] = useState(false);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState({ name: "displayOrder", direction: "asc" });

  const BACKEND_URL = process.env.REACT_APP_BACKEND;

  useEffect(() => {
    const currentPath = location.pathname;
    const matchingNavItem = navData.find((item) => item.link === currentPath);
    setWrite(!!matchingNavItem?.write);
  }, [location.pathname, navData]);

  const getAllHotels = useCallback((pageIndex, searchText, sort) => {
    Axios.post(`${BACKEND_URL}get-featured-deals`, {
      page: pageIndex + 1,
      limit: 10,
      search: searchText,
      sortField: sort?.name || "",
      sortOrder: sort?.direction || "",
    })
      .then((res) => {
        setAllHotels(res?.data?.data?.docs || []);
        setCount(res?.data?.data?.totalDocs || 0);
      })
      .catch((err) => showError(err));
  }, [BACKEND_URL]);

  useEffect(() => {
    window.scrollTo(0, 0);
    getAllHotels(page, search, sortOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAllHotels]);

  const showError = (err) => {
    toast.error(err?.response?.data?.message || "Something went wrong");
  };

  const computeDaysUntilExpiry = (dealStartDate) => {
    if (!dealStartDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(dealStartDate);
    startDate.setHours(0, 0, 0, 0);
    return Math.ceil((startDate - today) / (1000 * 60 * 60 * 24));
  };

  const sortedHotels = useMemo(() => allHotels.map((h) => ({
    ...h,
    daysUntilExpiry: computeDaysUntilExpiry(h.dealStartDate),
  })), [allHotels]);

  const handleFeaturedHotels = (event, id) => {
    Axios.post(`${BACKEND_URL}update-featured-deal`, {
      listingStatus: event.target.checked,
      id,
    })
      .then((res) => {
        getAllHotels(page, search, sortOrder);
        toast.success(res?.data?.message || "Updated successfully");
      })
      .catch((err) => showError(err));
  };

  const deleteHotel = () => {
    Axios.post(`${BACKEND_URL}delete-featured-deal`, { id: opendel.id })
      .then((res) => {
        getAllHotels(page, search, sortOrder);
        setOpendel({ status: false, id: null });
        toast.success(res?.data?.message || "Deleted successfully");
      })
      .catch((err) => showError(err));
  };

  const columns = [
    {
      name: "displayOrder",
      label: "Display Order",
      options: {
        customBodyRenderLite: (i) => sortedHotels[i]?.displayOrder ?? 0,
      },
    },
    {
      name: "heroImage",
      label: "Hotel Image",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (i) => (
          <img src={sortedHotels[i]?.heroImage} alt="" style={{ width: 60, height: 40, objectFit: "contain", backgroundColor: "#f0f0f0", borderRadius: 4 }} />
        ),
      },
    },
    {
      name: "hotelId",
      label: "Hotel Id",
    },
    {
      name: "name",
      label: "Hotel Name",
    },
    {
      name: "dealStartDate",
      label: "Start Date",
      options: {
        customBodyRenderLite: (i) => {
          const date = sortedHotels[i]?.dealStartDate;
          if (!date) return "Not set";
          const [y, m, d] = date.split("T")[0].split("-");
          return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString();
        },
      },
    },
    {
      name: "dealEndDate",
      label: "End Date",
      options: {
        customBodyRenderLite: (i) => {
          const date = sortedHotels[i]?.dealEndDate;
          if (!date) return "Not set";
          const [y, m, d] = date.split("T")[0].split("-");
          return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString();
        },
      },
    },
    {
      name: "numberOfNights",
      label: "Number of Nights",
      options: {
        customBodyRenderLite: (i) => sortedHotels[i]?.numberOfNights || 0,
      },
    },
    {
      name: "totalBookingAmount",
      label: "Total Booking Amount",
      options: {
        customBodyRenderLite: (i) => `$${parseFloat(sortedHotels[i]?.totalBookingAmount || 0).toFixed(2)}`,
      },
    },
    {
      name: "causePrice",
      label: "Cause Amount",
      options: {
        customBodyRenderLite: (i) => `$${parseFloat(sortedHotels[i]?.causePrice || 0).toFixed(2)}`,
      },
    },
    {
      name: "dealsType",
      label: "Deal Type",
      options: {
        customBodyRenderLite: (i) => {
          const deals = sortedHotels[i]?.dealsType;
          const dealsArray = Array.isArray(deals) ? deals : (deals ? [deals] : []);
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {dealsArray.map((deal, index) => (
                <Chip
                  key={index}
                  label={deal.charAt(0).toUpperCase() + deal.slice(1).replace('-', ' ')}
                  size="small"
                  color="primary"
                />
              ))}
            </Box>
          );
        },
      },
    },
    {
      name: "daysUntilExpiry",
      label: "Expiration",
      options: {
        filter: false,
        customBodyRender: (value) => {
          const status = getExpiryStatus(value);
          return <Chip label={status.label} size="small" color={status.color} />;
        },
      },
    },
    {
      name: "actions",
      label: "Action",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (i) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch
              checked={sortedHotels[i]?.listingStatus}
              onChange={(e) => handleFeaturedHotels(e, sortedHotels[i]?._id)}
              size="small"
            />
            <Edit
              sx={{ color: "#1976d2", fontSize: 20, cursor: "pointer" }}
              onClick={() => navigate(`/feature-deals/edit/${sortedHotels[i]?._id}`)}
            />
            <DeleteOutline
              sx={{ color: "red", fontSize: 20, cursor: "pointer" }}
              onClick={() => setOpendel({ status: true, id: sortedHotels[i]?._id })}
            />
          </Box>
        ),
      },
    },
  ];

  const options = {
    responsive: "standard",
    selectableRows: "none",
    filter: false,
    search: true,
    viewColumns: false,
    serverSide: true,
    rowsPerPageOptions: [],
    count,
    page,
    sortOrder,
    download: false,
    onTableChange: (action, tableState) => {
      if (action === "changePage") {
        setPage(tableState.page);
        getAllHotels(tableState.page, search, sortOrder);
      } else if (action === "sort") {
        const newSortOrder = tableState.sortOrder;
        setSortOrder(newSortOrder);
        setPage(0);
        getAllHotels(0, search, newSortOrder);
      }
    },
    onSearchChange: (searchText) => {
      const searchValue = searchText || "";
      setSearch(searchValue);
      setPage(0);
      getAllHotels(0, searchValue, sortOrder);
    },
  };

  return (
    <div className="container-fluid">
      <Helmet>
        <title>Featured Deals</title>
      </Helmet>
      <div className="d-flex justify-content-between align-center mb-3">
        <h2>Featured Deals List</h2>
        <Button variant="contained" onClick={() => navigate("/feature-deals/add")}>
          Add a Deal
        </Button>
      </div>

      <div className="table-responsive">
        <MUIDataTable
          title="Featured Deals"
          data={sortedHotels}
          columns={columns}
          options={options}
        />
      </div>

      {/* Delete Hotel Dialog */}
      <Dialog
        open={opendel.status}
        onClose={() => setOpendel({ status: false, id: null })}
        fullWidth
      >
        <div className="d-flex justify-content-between align-center p-3">
          <h3>Delete a Deal</h3>
          <IconButton onClick={() => setOpendel({ status: false, id: null })}>
            <Close />
          </IconButton>
        </div>
        <DialogContent dividers>
          Are you sure you want to delete this deal?
        </DialogContent>
        <DialogActions>
          <Button
            className="cancel-button"
            onClick={() => setOpendel({ status: false, id: null })}
          >
            Cancel
          </Button>
          <Button
            className="submit-button"
            variant="contained"
            onClick={deleteHotel}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FeaturedHotels;
